/**
 * Cart Item Reorderer
 * Automatically reorders cart item components based on ordinal indicators
 * ALBAU-207 - "Cart | Incorrect display order"
 */

const CartReorderer = (() => {
  const ORDINAL_MAP: Record<string, number> = {
    first: 1,
    second: 2,
    third: 3,
    fourth: 4,
    fifth: 5,
    sixth: 6,
    seventh: 7,
    eighth: 8,
    ninth: 9,
    tenth: 10,
    eleventh: 11,
    twelfth: 12,
    thirteenth: 13,
    fourteenth: 14,
    fifteenth: 15,
    sixteenth: 16,
    seventeenth: 17,
    eighteenth: 18,
    nineteenth: 19,
    twentieth: 20,
  };

  const CONFIG = {
    containerSelector: '.cart-item__components',
    itemSelector: '.cart-item__item-component',
    labelSelector: '.cart-item__item-component-label',
    debounceDelay: 100,
    watchSelectors: ['cart-item__components', 'cart-item__item-component', 'cart-drawer', 'drawer'],
    customEvents: ['cart-updated', 'CART_DRAWER_UPDATED', 'gwp:event', 'PRODUCT_ADD_TO_CART_SUCCESS'],
  } as const;

  let observer: MutationObserver | null = null;
  let debounceTimer: number | null = null;

  /**
   * Extract order value from text
   */
  function extractOrderValue(text: string): number {
    if (!text) return Infinity;

    const cleaned = text.trim().toLowerCase();

    // Check for ordinal words
    for (const [word, value] of Object.entries(ORDINAL_MAP)) {
      if (cleaned.includes(word)) return value;
    }

    // Check for numeric patterns
    const numericMatch = cleaned.match(/(\d+)/);
    if (numericMatch) return parseInt(numericMatch[1], 10);

    return Infinity;
  }

  /**
   * Reorder items within a single container
   */
  function reorderContainer(container: HTMLElement): void {
    const items = Array.from(
      container.querySelectorAll<HTMLElement>(CONFIG.itemSelector)
    );

    if (items.length === 0) return;

    // Build sorted array with order values
    const sorted = items
      .map((element) => {
        const label = element.querySelector<HTMLElement>(CONFIG.labelSelector);
        const labelText = label?.textContent || '';
        return {
          element,
          order: extractOrderValue(labelText),
        };
      })
      .sort((a, b) => a.order - b.order);

    // Check if already in order
    if (sorted.every((item, i) => item.element === items[i])) return;

    // Reorder in DOM
    sorted.forEach(({ element }) => container.appendChild(element));
  }

  /**
   * Reorder all cart item containers
   */
  function reorder(): void {
    const containers = document.querySelectorAll<HTMLElement>(CONFIG.containerSelector);
    containers.forEach(reorderContainer);
  }

  /**
   * Debounced reorder
   */
  function reorderDebounced(): void {
    if (debounceTimer) clearTimeout(debounceTimer);
    debounceTimer = window.setTimeout(reorder, CONFIG.debounceDelay);
  }

  /**
   * Check if mutation affects cart items
   */
  function isCartRelated(target: Node): boolean {
    let node = target as HTMLElement;
    while (node && node !== document.body) {
      if (node.classList) {
        for (const selector of CONFIG.watchSelectors) {
          if (node.classList.contains(selector)) return true;
        }
      }
      node = node.parentElement as HTMLElement;
    }
    return false;
  }

  /**
   * Initialize the reorderer
   */
  function init(): void {
    // Run initial reorder
    reorder();

    // Set up observer
    observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.addedNodes.length > 0 || mutation.removedNodes.length > 0) {
          if (isCartRelated(mutation.target)) {
            reorderDebounced();
            return;
          }
        }
      }
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });

    // Listen for custom events
    CONFIG.customEvents.forEach((event) => {
      document.addEventListener(event, reorderDebounced);
    });
  }

  /**
   * Cleanup
   */
  function destroy(): void {
    observer?.disconnect();
    observer = null;
    if (debounceTimer) clearTimeout(debounceTimer);
    CONFIG.customEvents.forEach((event) => {
      document.removeEventListener(event, reorderDebounced);
    });
  }

  // Auto-initialize
  if (typeof window !== 'undefined') {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', init);
    } else {
      init();
    }
  }

  // Public API
  return {
    init,
    destroy,
    reorder,
  };
})();

// Attach to window for global access
if (typeof window !== 'undefined') {
  (window as any).CartReorderer = CartReorderer;
}

// Also export for module usage
export default CartReorderer;